// ============================================================
// POI & 场景面 分类 — 颜色 & 显示配置（唯一数据源）
// 所有图例、标记、标签用色均从此处导出，保持全局一致
// ============================================================

export interface PoiSceneDef {
  /** 显示名称（中文） */
  label: string
  /** 主色 hex（对应 marker 填充、图例圆点） */
  color: string
  /** 深色 hex（对应 marker 边框、选中加深） */
  darkColor: string
  /** Tailwind 背景色类名 */
  bg: string
  /** Tailwind 文字色类名 */
  text: string
  /** Tailwind 边框色类名 */
  border: string
}

export const POI_SCENE_MAP: Record<string, PoiSceneDef> = {
  public_facility: {
    label: '公共设施',
    color: '#64748b',
    darkColor: '#475569',
    bg: 'bg-slate-500',
    text: 'text-slate-600',
    border: 'border-slate-200',
  },
  hotel: {
    label: '宾馆酒店',
    color: '#e11d48',
    darkColor: '#be123c',
    bg: 'bg-rose-500',
    text: 'text-rose-600',
    border: 'border-rose-200',
  },
  school: {
    label: '学校',
    color: '#eab308',
    darkColor: '#ca8a04',
    bg: 'bg-yellow-500',
    text: 'text-yellow-600',
    border: 'border-yellow-200',
  },
  scenic: {
    label: '景区',
    color: '#14b8a6',
    darkColor: '#0d9488',
    bg: 'bg-teal-500',
    text: 'text-teal-600',
    border: 'border-teal-200',
  },
  commercial: {
    label: '商务楼宇',
    color: '#ef4444',
    darkColor: '#dc2626',
    bg: 'bg-red-500',
    text: 'text-red-600',
    border: 'border-red-200',
  },
  transportation: {
    label: '交通枢纽',
    color: '#06b6d4',
    darkColor: '#0891b2',
    bg: 'bg-cyan-500',
    text: 'text-cyan-600',
    border: 'border-cyan-200',
  },
  government: {
    label: '政企单位',
    color: '#3b82f6',
    darkColor: '#2563eb',
    bg: 'bg-blue-500',
    text: 'text-blue-600',
    border: 'border-blue-200',
  },
  residential: {
    label: '居民区',
    color: '#10b981',
    darkColor: '#059669',
    bg: 'bg-emerald-500',
    text: 'text-emerald-600',
    border: 'border-emerald-200',
  },
  industrial: {
    label: '厂园区',
    color: '#f97316',
    darkColor: '#ea580c',
    bg: 'bg-orange-500',
    text: 'text-orange-600',
    border: 'border-orange-200',
  },
  shopping: {
    label: '商圈',
    color: '#d946ef',
    darkColor: '#c026d3',
    bg: 'bg-fuchsia-500',
    text: 'text-fuchsia-600',
    border: 'border-fuchsia-200',
  },
  other: {
    label: '其他',
    color: '#6b7280',
    darkColor: '#4b5563',
    bg: 'bg-gray-500',
    text: 'text-gray-600',
    border: 'border-gray-200',
  },
} as const

export const DEFAULT_SCENE_KEY = 'other'

/** 根据 poiScene 的 key 获取配置 */
export const getPoiSceneDef = (key?: string): PoiSceneDef => {
  return POI_SCENE_MAP[key ?? ''] ?? POI_SCENE_MAP[DEFAULT_SCENE_KEY]
}

/**
 * 根据中文标签反向查找 key
 *
 * 优先精确匹配工作记录中定义的 11 类场景分类，
 * 再通过关键词模糊匹配兼容历史数据。
 */
export const getSceneKeyByLabel = (label?: string): string => {
  if (!label) return 'other'
  const t = label.toLowerCase()
  // 场景分类 — 精确匹配（11 类固定名称）
  if (t === '公共设施') return 'public_facility'
  if (t === '宾馆酒店') return 'hotel'
  if (t === '学校') return 'school'
  if (t === '景区') return 'scenic'
  if (t === '商务楼宇') return 'commercial'
  if (t === '交通枢纽') return 'transportation'
  if (t === '政企单位') return 'government'
  if (t === '居民区') return 'residential'
  if (t === '厂园区') return 'industrial'
  if (t === '商圈') return 'shopping'
  if (t === '其他') return 'other'
  // POI 分类 — 精确匹配（7 类固定名称，映射到场景大类）
  if (t === '居民小区') return 'residential'
  if (t === '聚类市场') return 'shopping'
  if (t === '农村住宅') return 'residential'
  if (t === '企事业单位') return 'government'
  if (t === '沿街商铺') return 'shopping'
  return 'other'
}

/** 获取 hex 主色 */
export const getCategoryHexColor = (poiScene?: string): string => {
  return getPoiSceneDef(poiScene).color
}

/** 获取 hex 深色 */
export const getCategoryDarkColor = (poiScene?: string): string => {
  return getPoiSceneDef(poiScene).darkColor
}

/** 获取 Tailwind 背景类 */
export const getCategoryBgClass = (poiScene?: string): string => {
  return getPoiSceneDef(poiScene).bg
}

/** 获取 Tailwind 标签样式组合 */
export const getTagStyleClasses = (poiScene?: string): { bg: string; text: string; border: string } => {
  const def = getPoiSceneDef(poiScene)
  return { bg: def.bg, text: def.text, border: def.border }
}

/** 按显示名称匹配的场景配置（用于图例/统计） */
export const getPoiSceneDefByLabel = (label?: string): PoiSceneDef => {
  const key = getSceneKeyByLabel(label)
  return POI_SCENE_MAP[key]
}
